import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./interceptors/axios";
import * as Sentry from "@sentry/react";
import ErrorFallback from "./components/ErrorFallback";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

// Initialize Sentry for error tracking and performance monitoring
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (sentryDsn) {
  const tracesSampleRate = Number(
    process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1"
  );
  const profilesSampleRate = Number(
    process.env.REACT_APP_SENTRY_PROFILES_SAMPLE_RATE || "0.0"
  );
  Sentry.init({
    dsn: sentryDsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "production",
    release: process.env.REACT_APP_SENTRY_RELEASE,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    // Performance monitoring
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.1,
    // Browser profiling uses profileSessionSampleRate (not profilesSampleRate)
    profileSessionSampleRate: Number.isFinite(profilesSampleRate)
      ? profilesSampleRate
      : 0.0,
    // Auto-profile during traces (default is 'manual' which requires explicit start/stop)
    profileLifecycle: 'trace',
    // Enable debug mode in development
    debug: process.env.NODE_ENV === 'development',
    // Session replay: 10% of normal sessions, 100% of error sessions
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // PII filtering: strip sensitive fields from events
    beforeSend(event, hint) {
      // Define sensitive field patterns to filter out
      const sensitivePatterns = [
        /password/i,
        /token/i,
        /ssn/i,
        /social.?security/i,
        /credit.?card/i,
        /cvv/i,
        /account.?number/i,
        /routing.?number/i,
        /api.?key/i,
        /secret/i,
      ];

      // Filter function to check if key matches sensitive patterns
      const isSensitive = (key) =>
        sensitivePatterns.some((pattern) => pattern.test(key));

      // Recursively filter sensitive data from objects
      const filterObject = (obj) => {
        if (!obj || typeof obj !== "object") return obj;

        if (Array.isArray(obj)) {
          return obj.map(filterObject);
        }

        const filtered = {};
        for (const [key, value] of Object.entries(obj)) {
          if (isSensitive(key)) {
            filtered[key] = "[Filtered]";
          } else if (typeof value === "object") {
            filtered[key] = filterObject(value);
          } else {
            filtered[key] = value;
          }
        }
        return filtered;
      };

      // Filter request data
      if (event.request) {
        event.request = filterObject(event.request);
      }

      // Filter breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
          ...breadcrumb,
          data: filterObject(breadcrumb.data),
        }));
      }

      // Filter extra data
      if (event.extra) {
        event.extra = filterObject(event.extra);
      }

      return event;
    },
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog={true}>
    <App />
  </Sentry.ErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
